import type { PosSettings, ReceiptData } from '@/types';
import { useTranslation } from 'react-i18next';
import { getSellerTaxLines, shouldShowFbrBlock, shouldShowZatcaBlock, getZatcaReceiptSummary } from '@/utils/receiptCompliance';

export { companyContactParts } from '@/utils/receiptCompliance';

/** Seller tax IDs (NTN/STRN or VAT/EIN). */
export function SellerIdsBlock({
  posSettings,
  className = 'text-xs text-gray-600 space-y-0.5 mt-1',
  align = 'left',
}: {
  posSettings?: PosSettings | null;
  className?: string;
  align?: 'left' | 'center';
}) {
  const lines = getSellerTaxLines(posSettings);
  if (!lines.length) return null;
  return (
    <div className={`${className} ${align === 'center' ? 'text-center' : ''}`}>
      {lines.map((l) => (
        <div key={l.label}>
          <span className="font-semibold">{l.label}:</span> {l.value}
        </div>
      ))}
    </div>
  );
}

/** FBR digital invoice block — Pakistan only. */
export function FiscalBlock({
  inv,
  posSettings,
  compact = false,
}: {
  inv?: ReceiptData | null;
  posSettings?: PosSettings | null;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (!shouldShowFbrBlock(inv, posSettings)) return null;

  const pending = inv?.fbr_status === 'Pending' && !inv?.fbr_invoice_number;
  const qrData = inv?.fbr_qr_code
    || (inv?.fbr_invoice_number
      ? `FBR_INV:${inv.fbr_invoice_number}|USIN:${inv.fbr_usin || ''}`
      : '');

  return (
    <div className={`w-full ${compact ? 'mt-2 pt-2 border-t border-dashed border-gray-300' : 'mt-4 pt-3 border-t border-dashed border-gray-300'}`}>
      {!compact && (
        <div className="flex items-center justify-around gap-2 my-1">
          <div className="fbr-logo-container flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 150 100" width="90" height="60">
              <path d="M 20 40 C 40 20, 110 20, 130 40" fill="none" stroke="#2e7d32" strokeWidth="3" strokeLinecap="round" />
              <path d="M 25 43 C 42 25, 108 25, 125 43" fill="none" stroke="#0d47a1" strokeWidth="1.5" strokeLinecap="round" />
              <text x="75" y="44" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="22" fill="#0d47a1" textAnchor="middle">FBR</text>
              <rect x="15" y="52" width="120" height="42" rx="6" fill="#0d47a1" />
              <text x="75" y="76" fontFamily="Helvetica, Arial, sans-serif" fontWeight="900" fontSize="18" fill="#ffffff" textAnchor="middle">DIGITAL</text>
              <text x="75" y="88" fontFamily="Helvetica, Arial, sans-serif" fontWeight="bold" fontSize="7" fill="#ffffff" textAnchor="middle">INVOICING SYSTEM</text>
            </svg>
          </div>
          {qrData ? (
            <div className="p-1 bg-white border border-gray-200 rounded flex flex-col items-center shadow-sm">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`}
                alt="FBR Verification QR"
                style={{ width: '0.9in', height: '0.9in' }}
                className="object-contain"
              />
              <div className="text-[8px] text-slate-400 mt-0.5 font-mono">{t('receipt.verifyTaxAsaan')}</div>
            </div>
          ) : null}
        </div>
      )}
      {compact && qrData ? (
        <div className="flex justify-center mb-1">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`}
            alt="FBR QR"
            className="w-16 h-16 object-contain"
          />
        </div>
      ) : null}
      <div className={`font-mono text-center text-slate-800 ${compact ? 'text-[9px]' : 'text-[10px] mt-2'}`}>
        <span className="font-bold">{t('receipt.fbrInv')}:</span>{' '}
        {inv?.fbr_invoice_number || (pending ? t('receipt.fbrPending') : '—')}
        {inv?.fbr_usin ? (
          <span className="block text-[9px] text-slate-500 mt-0.5">
            <span className="font-semibold">USIN:</span> {inv.fbr_usin}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** ZATCA Fatoora block — Saudi Arabia only (Phase 2 simplified / credit note). */
export function ZatcaBlock({
  inv,
  posSettings,
  compact = false,
}: {
  inv?: ReceiptData | null;
  posSettings?: PosSettings | null;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  if (!shouldShowZatcaBlock(inv, posSettings)) return null;

  const pending = inv?.zatca_status === 'Pending' && !inv?.zatca_uuid;
  const qrData = inv?.zatca_qr_code || '';
  const summary = inv ? getZatcaReceiptSummary(inv, posSettings) : null;
  const docTitle = summary ? t(summary.documentTitleKey) : '';

  return (
    <div className={`w-full ${compact ? 'mt-2 pt-2 border-t border-dashed border-gray-300' : 'mt-4 pt-3 border-t border-dashed border-gray-300'}`}>
      {docTitle ? (
        <div className={`font-bold text-emerald-900 text-center ${compact ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>
          {docTitle}
        </div>
      ) : null}
      {!compact && (
        <div className="flex items-center justify-around gap-2 my-1">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="text-emerald-800 font-bold text-sm">ZATCA</div>
              <div className="text-[9px] text-gray-500">{t('receipt.zatcaPhase2')}</div>
            </div>
          </div>
          {qrData ? (
            <div className="p-1 bg-white border border-gray-200 rounded flex flex-col items-center shadow-sm">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`}
                alt="ZATCA Verification QR"
                style={{ width: '0.9in', height: '0.9in' }}
                className="object-contain"
              />
              <div className="text-[8px] text-slate-400 mt-0.5">{t('receipt.verifyQr')}</div>
            </div>
          ) : null}
        </div>
      )}
      {compact && qrData ? (
        <div className="flex justify-center mb-1">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`}
            alt="ZATCA QR"
            className="w-16 h-16 object-contain"
          />
        </div>
      ) : null}
      {summary ? (
        <div className={`text-center text-slate-700 space-y-0.5 ${compact ? 'text-[9px]' : 'text-[10px] mt-1'}`}>
          {summary.sellerVat ? (
            <div><span className="font-semibold">{t('receipt.vatTrn')}:</span> {summary.sellerVat}</div>
          ) : null}
          <div><span className="font-semibold">{t('receipt.invoiceDateTime')}:</span> {summary.invoiceDateTime}</div>
          <div><span className="font-semibold">{t('receipt.totalWithVat')}:</span> {summary.totalWithVat.toFixed(2)}</div>
          <div><span className="font-semibold">{t('receipt.vatAmount')}:</span> {summary.vatAmount.toFixed(2)}</div>
        </div>
      ) : null}
      <div className={`font-mono text-center text-slate-800 ${compact ? 'text-[9px]' : 'text-[10px] mt-2'}`}>
        <span className="font-bold">{t('receipt.zatcaUuid')}:</span>{' '}
        {inv?.zatca_uuid || (pending ? t('receipt.zatcaPending') : '—')}
        {inv?.zatca_icv ? (
          <span className="block text-[9px] text-slate-500 mt-0.5">
            <span className="font-semibold">{t('receipt.zatcaIcv')}:</span> {inv.zatca_icv}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function PricesIncludeTaxNote({ posSettings }: { posSettings?: PosSettings | null }) {
  const { t } = useTranslation();
  if (!posSettings?.prices_include_tax) return null;
  return <div className="text-[10px] text-gray-400 mt-1">{t('receipt.pricesIncludeTax')}</div>;
}

export function DeliveryBlock({
  inv,
  compact = false,
}: {
  inv?: ReceiptData | null;
  compact?: boolean;
}) {
  if (inv?.fulfillment_type !== 'Delivery') return null;

  return (
    <div
      className={`w-full rounded border border-amber-300 bg-amber-50/70 p-2 text-left text-gray-800 ${
        compact ? 'my-1 text-[10px]' : 'my-2 text-xs'
      }`}
    >
      <div className="flex items-center justify-between font-bold text-amber-900 mb-1">
        <span>🚚 DELIVERY ORDER</span>
        {inv.delivery_status && (
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 uppercase">
            {inv.delivery_status}
          </span>
        )}
      </div>
      <div className="space-y-0.5 text-gray-700">
        {inv.delivery_recipient_name && (
          <div>
            <span className="font-semibold text-gray-900">Recipient:</span> {inv.delivery_recipient_name}
          </div>
        )}
        {inv.delivery_phone && (
          <div>
            <span className="font-semibold text-gray-900">Phone:</span> {inv.delivery_phone}
          </div>
        )}
        {inv.delivery_address && (
          <div>
            <span className="font-semibold text-gray-900">Address:</span> {inv.delivery_address}
          </div>
        )}
        {inv.delivery_postal_code && (
          <div>
            <span className="font-semibold text-gray-900">Postal Code:</span> {inv.delivery_postal_code}
          </div>
        )}
        {inv.delivery_notes && (
          <div className="italic text-gray-600">
            <span className="font-semibold not-italic text-gray-900">Notes:</span> {inv.delivery_notes}
          </div>
        )}
      </div>
    </div>
  );
}

